import { NavLink } from "react-router-dom";
import {
  ShieldCheck,
  GitBranch,
  ScrollText,
  BarChart3,
  Server,
  PackageSearch,
  Radio,
  Terminal,
} from "lucide-react";
import { cn } from "@/lib/utils";

/** Central nav manifest — add a new page's entry here and it shows up in
 * the sidebar automatically. Grouped so the sidebar stays legible as this
 * grows past two pages (fleet metrics, topology, journal-tail, etc. are
 * all planned — see internal/management/README.md). */
interface NavItem {
  to: string;
  label: string;
  icon: typeof ShieldCheck;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Fleet",
    items: [
      // fleet-health-dashboard-plan.md — health/topology; Box Approvals
      // stays its own page (owner decision), cross-linked from Fleet.
      { to: "/fleet", label: "Fleet Health", icon: Server },
      { to: "/fleet/flow", label: "Fleet Flow", icon: GitBranch },
      { to: "/fleet/nats", label: "NATS", icon: Radio },
      { to: "/", label: "Box Approvals", icon: ShieldCheck },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/registry", label: "Registry", icon: PackageSearch },
      { to: "/dependencies", label: "Dependencies", icon: GitBranch },
      { to: "/logs", label: "Logs", icon: ScrollText },
      { to: "/worker-logs", label: "Worker Logs", icon: Terminal },
      { to: "/usage", label: "Usage", icon: BarChart3 },
    ],
  },
];

/**
 * Mobile-friendly nav (2026-09-04) — was a permanently-visible fixed w-60
 * `<aside>`, which on a phone-width viewport ate most of the screen with
 * no way to hide it. Now a controlled drawer everywhere: `open`/`onClose`
 * always apply, but only DO anything visually below the `md` breakpoint
 * (`md:translate-x-0 md:static` overrides the closed/open transform and
 * fixed positioning back to the original always-visible desktop layout) —
 * so desktop behavior is unchanged, mobile gets a slide-in panel + tap-
 * outside-to-close backdrop. AppShell owns the open/close state and the
 * hamburger button that toggles it.
 */
export function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 border-r bg-card flex flex-col transition-transform duration-200",
          "md:static md:z-auto md:w-60 md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="p-4 border-b">
          <div className="font-semibold text-lg">Pure</div>
          <div className="text-xs text-muted-foreground">Management</div>
        </div>
        <nav className="flex-1 p-2 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
